/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ImgixImage from 'components/ImgixImage/ImgixImage';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { usePrefersReducedMotion } from '@anatoliygatt/use-prefers-reduced-motion';
import useKeys from 'effects/keys';

import joinStrings from 'utils/joinStrings';
import useSlides from './hooks/useSlides';

import styles from './Carousel.module.scss';

/**
 * This is the Carousel component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Carousel({ heading, items, anchors, inverted }) {
  const prefersReducedMotion = usePrefersReducedMotion();

  const [slide, setSlide] = useSlides(items, 0);
  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5,
  });
  const [running, setRunning] = useState(!prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setSlide.setActive(false);
    } else {
      setSlide.setActive(inView && running);
    }
  }, [running, inView, prefersReducedMotion]);

  useEffect(() => {
    setRunning(!prefersReducedMotion);
  }, [prefersReducedMotion]);

  useKeys('animation', inView, {
    esc: () => {
      setRunning((prev) => !prev);
    },
  });

  const itemTitles = useMemo(() => {
    const arr = items.map((item) => item.title);
    return joinStrings(arr, 'og');
  }, [items]);

  if (!slide.current) {
    return null;
  }

  const progressOverall = (slide.index + slide.progress) / items.length;

  return (
    <div
      className={classNames('header__underneath-padder', styles.wrapper, {
        [styles.inverted]: inverted,
      })}
    >
      <div className="content-width">
        <div className={styles.main}>
          <div className={styles.content} ref={ref}>
            <h1 aria-label={`${heading} ${itemTitles}`}>
              {heading}{' '}
              {!prefersReducedMotion && (
                <TransitionGroup
                  component="span"
                  aria-hidden
                  role="presentation"
                >
                  <CSSTransition
                    key={slide.current.title}
                    classNames={styles.label}
                    timeout={
                      parseInt(styles.transitionDuration, 10) +
                      parseInt(styles.transitionDelay, 10)
                    }
                  >
                    <span className={styles.label}>{slide.current.title}</span>
                  </CSSTransition>
                </TransitionGroup>
              )}
              {prefersReducedMotion && (
                <span className={classNames(styles.label, styles.labelStill)}>
                  {slide.current.title}
                </span>
              )}
            </h1>
            <span className={styles.progressBar}>
              <span
                className={styles.progress}
                style={{ width: `${progressOverall * 100}%` }}
              />
            </span>
          </div>
          <div className={styles.imageWrapper}>
            <div className={styles.imageWidth}>
              {!prefersReducedMotion && (
                <TransitionGroup component="div" className={styles.imageAspect}>
                  <CSSTransition
                    key={slide.current.title}
                    classNames={styles.image}
                    timeout={
                      parseInt(styles.transitionDuration, 10) +
                      parseInt(styles.transitionDelay, 10)
                    }
                  >
                    <ImgixImage
                      {...slide.current.image}
                      eager
                      altText=""
                      className={styles.image}
                    />
                  </CSSTransition>
                </TransitionGroup>
              )}
              {prefersReducedMotion && (
                <ImgixImage
                  {...slide.current.image}
                  eager
                  altText=""
                  className={styles.image}
                />
              )}
            </div>
          </div>
          {anchors && anchors.length > 0 && (
            <ul className={styles.anchors}>
              {anchors.map((anchor, index) => (
                <li key={index}>
                  <a href={anchor.url}>
                    <span
                      className={classNames('widget__circled-arrow', {
                        'widget__circled-arrow-inverted': inverted,
                      })}
                    ></span>
                    <span>{anchor.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Carousel.propTypes = {
  heading: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        altTexT: PropTypes.string,
      }),
    })
  ),
  anchors: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        altTexT: PropTypes.string,
      }),
    })
  ),
  inverted: PropTypes.bool,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Carousel.defaultProps = {
  title: 'TODO',
  items: [],
  anchors: [],
  inverted: false,
};

export default Carousel;
